"use client";
import React, { useRef, useState, useEffect } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const EXPERIENCE_DATA = [
  {
    id: "exp-1",
    company: "Self-Employed",
    role: "Freelance Full-Stack Developer",
    date: "Mar 2023 - Present",
    bullets: [
      "Architecting an ongoing Enterprise Field Service Management (FSM) platform (Monorepo: Next.js, PostgreSQL, React Native) for a US client, currently specializing in HVAC engineering tools with a scalable foundation for multi-trade expansion.",
      "Developed the CommuniGuard Residential System App using MERN and Expo, integrating Supabase and Google Maps for real-time tracking and automated emergency broadcasting.",
      "Engineered comprehensive POS systems and secure property management platforms featuring dynamic inventory deduction, automated billing, and role-based access control.",
      "Managed CI/CD pipelines and self-hosted infrastructure using Hostinger VPS and Coolify to reliably deploy and maintain multiple client applications.",
    ],
  },
  {
    id: "exp-2",
    company: "BEfied (fka Edufied)| Singapore",
    role: "Frontend Developer (OJT)",
    date: "Jan - Jul (600 hours)", // Note: Add the specific year here if desired
    bullets: [
      "Translated complex Figma UI designs into responsive web interfaces for a tamper-proof blockchain verification system using Bootstrap and Nunjucks.",
      "Operated in an Agile environment using Jira and Bitbucket, collaborating directly with backend developers, PMs, and QA engineers.",
      "Acted as the sole frontend developer during a critical project phase, successfully onboarding and mentoring incoming developers on project architecture.",
    ],
  },
];

const ExperienceA: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [height, setHeight] = useState(0);

  // Calculate the total height of the timeline list to constrain our line
  useEffect(() => {
    if (listRef.current) {
      const rect = listRef.current.getBoundingClientRect();
      setHeight(rect.height);
    }
  }, [listRef]);

  // Framer motion scroll tracking
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 30%", "end 70%"], // Adjusted offset to match the line fill speed with viewport center
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  useGSAP(
    () => {
      const items = gsap.utils.toArray<HTMLLIElement>(".timeline-item");

      items.forEach((item) => {
        // 1. Fade in the overall card
        gsap.fromTo(
          item,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power3.out",
            scrollTrigger: {
              trigger: item,
              start: "top 80%",
            },
          },
        );

        // 2. Light up the dot as the scroll line passes it
        const dot = item.querySelector(".timeline-dot");
        const innerDot = item.querySelector(".timeline-inner-dot");

        if (dot && innerDot) {
          gsap.to([dot, innerDot], {
            borderColor: "#34abe1", // Using your primary hex directly for safety, or use var(--fallback-p)
            backgroundColor: "#34abe1",
            boxShadow: "0 0 15px rgba(52, 171, 225, 0.5)",
            duration: 0.4,
            ease: "power2.out",
            scrollTrigger: {
              trigger: item,
              start: "top 50%", // Triggers exactly when the item hits the center of the screen
              toggleActions: "play none none reverse", // Reverses if the user scrolls back up
            },
          });
        }
      });
    },
    { scope: containerRef },
  );

  return (
    <section
      id="experience"
      className="w-full max-w-5xl py-24 mx-auto px-4"
      ref={containerRef}
    >
      <div className="flex items-center gap-4 mb-16">
        <h2 className="text-3xl font-bold md:text-4xl text-base-content">
          Where I've Worked
        </h2>
        <div className="flex-1 h-px bg-base-content/20"></div>
      </div>

      <div className="relative pb-20">
        <ul
          ref={listRef}
          className="timeline timeline-snap-icon max-md:timeline-compact timeline-vertical relative z-10"
        >
          {EXPERIENCE_DATA.map((job, index) => {
            const isEven = index % 2 === 0;

            return (
              <li key={job.id} className="timeline-item group pb-10">
                <div className="timeline-middle">
                  <div className="sticky top-1/2 z-40 flex items-center justify-center">
                    {/* The Dot container - added custom classes for GSAP targeting */}
                    <div className="timeline-dot h-9 w-9 relative z-20 rounded-full bg-base-200 border-3 border-base-content/20 flex items-center justify-center transition-all duration-70 group-hover:scale-110">
                      <div className="timeline-inner-dot h-2.5 w-2.5 rounded-full bg-base-content/30 transition-all duration-70" />

                      <div className="absolute inset-0 rounded-full blur-md opacity-0 group-hover:opacity-70 bg-primary transition"></div>
                    </div>
                  </div>
                </div>

                <div
                  className={`${isEven ? "timeline-start md:text-end" : "timeline-end"} mb-10 px-4 md:px-8 w-full`}
                >
                  <div className="sticky top-1/2 z-30 bg-base-100/90 backdrop-blur-md pb-4 pt-2 -mt-4 rounded-xl px-4 shadow-sm border border-base-content/5">
                    <time className="font-mono italic text-sm text-base-content/60">
                      {job.date}
                    </time>
                    <h3 className="text-xl font-bold mt-1 text-base-content leading-tight">
                      {job.role}
                    </h3>
                    <h4 className="text-lg font-medium text-primary mb-2">
                      {job.company}
                    </h4>

                    <ul className="space-y-2 text-left inline-block mt-4">
                      {job.bullets.map((bullet, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-2 text-base-content/80"
                        >
                          <span className="text-primary text-xs mt-1">▹</span>
                          <span className="text-sm md:text-base">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Removed daisyUI <hr /> completely to let the Framer Motion line do the work */}
              </li>
            );
          })}
        </ul>

        {/* The Continuous Aceternity-style Line */}
        {/* Adjusted left positioning to align perfectly with daisyUI dots: left-[28px] on mobile, left-1/2 on md */}
        <div
          style={{ height: height + "px" }}
          className="absolute left-[18px] md:left-1/2 top-0 w-[4px] -translate-x-1/2 bg-base-content/10 z-0 block pointer-events-none"
        >
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            // Changed gradient to match your primary blue theme instead of purple/blue
            className="absolute inset-x-0 top-0 w-[4px] bg-gradient-to-t from-primary via-[#34abe1] to-transparent rounded-full shadow-[0_0_20px_rgba(52,171,225,0.8)]"
          />
        </div>
      </div>
    </section>
  );
};

export default ExperienceA;
